import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, X, Plus, Users, Calendar, Crown, AlertCircle } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import { Card, CardContent } from '@/components/ui/card';
import { trackPageView, trackClick, trackEvent, EventTypes } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionSetup = () => {
  const [searchParams] = useSearchParams();
  const editSessionId = searchParams.get('edit');
  const draftIdParam = searchParams.get('draft');
  const isEditMode = !!editSessionId;
  const isDraftMode = !!draftIdParam;
  
  const [topic, setTopic] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [timeLimit, setTimeLimit] = useState(5); // in minutes
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [maxParticipants, setMaxParticipants] = useState(10);
  const [useEndDatetime, setUseEndDatetime] = useState(false);
  const [endDatetime, setEndDatetime] = useState('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [draftId, setDraftId] = useState(draftIdParam || null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false); // Public by default
  const autoSaveTimerRef = useRef(null);
  const navigate = useNavigate();

  const timeLimitOptions = [1, 2, 5, 10];

  // Auto-save function with debounce
  const autoSaveDraft = async () => {
    // Don't auto-save in edit mode
    if (isEditMode) return;
    
    try {
      setAutoSaving(true);
      const token = localStorage.getItem('token');
      const sessionData = {
        topic: topic || 'Untitled Session',
        hashtags: hashtags.length > 0 ? hashtags : ['brainstorm'],
        time_limit: timeLimit * 60,
        is_anonymous: isAnonymous,
        max_participants: maxParticipants,
        end_datetime: useEndDatetime ? endDatetime : null,
        hidden_session: isPrivate
      };

      if (draftId) {
        // Update existing draft
        await axios.put(
          `${API}/sessions/${draftId}`,
          sessionData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } else {
        // Create new draft
        const response = await axios.post(
          `${API}/sessions`,
          sessionData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setDraftId(response.data.id);
        console.log('✓ Draft created:', response.data.id);
      }
      
      setLastSaved(new Date());
      setAutoSaving(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaving(false);
    }
  };

  // Trigger auto-save when form data changes
  useEffect(() => {
    // Skip auto-save in edit mode
    if (isEditMode) return;
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Only auto-save if we have minimum data
    if (topic || hashtags.length > 0) {
      autoSaveTimerRef.current = setTimeout(() => {
        autoSaveDraft();
      }, 2000); // Save 2 seconds after last change
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [topic, hashtags, timeLimit, isAnonymous, maxParticipants, useEndDatetime, endDatetime]);

  useEffect(() => {
    trackPageView('session_setup');
    fetchProfile();
    if (isEditMode) {
      loadExistingSession();
    } else if (isDraftMode) {
      loadDraftFromUrl();
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Update localStorage to ensure fresh data across app
      localStorage.setItem('facilitator', JSON.stringify(response.data));
      
      // Check session limit (only for new sessions, not edits)
      if (!isEditMode && !response.data.is_paid && response.data.sessions_created_count >= 10) {
        setShowLimitModal(true);
      }
    } catch (error) {
      console.error('Failed to load profile');
    }
  };

  const loadExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${editSessionId}`);
      const session = response.data;
      
      setTopic(session.topic);
      setHashtags(session.hashtags || []);
      setTimeLimit(Math.floor(session.time_limit / 60)); // convert from seconds to minutes
      setIsAnonymous(session.is_anonymous);
      setMaxParticipants(session.max_participants || 10);
      
      // Check if session uses end_datetime
      if (session.end_datetime) {
        setUseEndDatetime(true);
        // Format datetime for input field (YYYY-MM-DDTHH:MM)
        const date = new Date(session.end_datetime);
        const formatted = date.toISOString().slice(0, 16);
        setEndDatetime(formatted);
      }
    } catch (error) {
      toast.error('Failed to load session data');
      navigate('/dashboard');
    }
  };

  const loadDraftFromUrl = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions/${draftIdParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const draftData = response.data;
      
      // Load all fields from the draft
      setTopic(draftData.topic || '');
      setHashtags(draftData.hashtags || []);
      setTimeLimit(Math.floor(draftData.time_limit / 60));
      setIsAnonymous(draftData.is_anonymous !== undefined ? draftData.is_anonymous : true);
      setMaxParticipants(draftData.max_participants || 10);
      
      // Check if draft uses end_datetime
      if (draftData.end_datetime) {
        setUseEndDatetime(true);
        const date = new Date(draftData.end_datetime);
        const formatted = date.toISOString().slice(0, 16);
        setEndDatetime(formatted);
      }
      
      toast.success('Draft loaded successfully!');
    } catch (error) {
      console.error('Failed to load draft:', error);
      toast.error('Failed to load draft');
    }
  };

  const addHashtag = () => {
    if (hashtagInput.trim() && !hashtags.includes(hashtagInput.trim())) {
      setHashtags([...hashtags, hashtagInput.trim()]);
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(t => t !== tag));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    if (hashtags.length === 0) {
      toast.error('Please add at least one hashtag');
      return;
    }

    // Validate end datetime if using that mode
    if (useEndDatetime && !endDatetime) {
      toast.error('Please select an end date and time');
      return;
    }

    // Check session limit before creating (only for new sessions)
    if (!isEditMode && !profile?.is_paid && profile?.sessions_created_count >= 10) {
      setShowLimitModal(true);
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (isEditMode) {
        // Update existing session
        const sessionData = {
          topic,
          hashtags,
          time_limit: timeLimit * 60,
          is_anonymous: isAnonymous,
          max_participants: maxParticipants,
          end_datetime: useEndDatetime ? endDatetime : null,
          hidden_session: isPrivate
        };
        
        const response = await axios.put(
          `${API}/sessions/${editSessionId}`,
          sessionData,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success('Session updated successfully!');
        navigate(`/session/${response.data.id}/facilitator`);
      } else {
        // First, ensure draft is saved
        if (!draftId) {
          await autoSaveDraft();
        }
        
        // Publish the draft
        await axios.post(
          `${API}/sessions/${draftId}/publish`,
          {},
          { headers: { Authorization: `Bearer ${token}` }}
        );

        toast.success('Session created successfully!');
        navigate(`/session/${draftId}/facilitator`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} session`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex justify-between items-center mb-4">
          <PollarBrandMini />
        </div>
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to Dashboard
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#1a1a1a' }}>
            {isEditMode ? 'Edit Session' : 'Setup Ideation Session'}
          </h1>
          <p className="text-gray-600 mb-8">
            {isEditMode ? 'Update your session configuration' : 'Configure your brainstorming session'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Private/Public Toggle */}
            <div>
              <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl border-2 border-purple-200">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    {isPrivate ? '🔒 Private Session' : '🌐 Public Session'}
                    {isPrivate && !profile?.is_paid && (
                      <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white ml-2">
                        <Crown className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </Label>
                  <p className="text-sm text-gray-600 mt-1">
                    {isPrivate 
                      ? 'Only you can see this session. It will not appear in the community feed.'
                      : 'This session will be visible in the community feed after completion.'
                    }
                  </p>
                </div>
                <Switch
                  checked={isPrivate}
                  onCheckedChange={(checked) => {
                    if (checked && !profile?.is_paid) {
                      // Redirect to paywall
                      navigate('/subscription');
                      return;
                    }
                    setIsPrivate(checked);
                  }}
                />
              </div>
            </div>

            {/* Topic */}
            <div>
              <Label htmlFor="topic" className="text-sm font-medium text-gray-700 mb-2 block">
                Topic of Ideation
              </Label>
              <Input
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="E.g., How can we improve customer experience?"
                className="text-lg p-6 rounded-xl"
                data-testid="topic-input"
              />
            </div>

            {/* Hashtags */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Hashtags
              </Label>
              <div className="flex gap-2 mb-3">
                <Input
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                  placeholder="Add a hashtag"
                  className="rounded-xl"
                  data-testid="hashtag-input"
                />
                <Button
                  type="button"
                  onClick={addHashtag}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-xl px-6"
                  data-testid="add-hashtag-button"
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag, idx) => (
                  <Badge key={idx} className="px-3 py-2 text-sm flex items-center gap-2" data-testid={`hashtag-${tag}`}>
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeHashtag(tag)}
                      className="hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Time Limit - Hidden when using End Date/Time */}
            {!useEndDatetime && (
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">
                  Time Limit
                </Label>
                <div className="grid grid-cols-4 gap-3">
                  {timeLimitOptions.map(minutes => (
                    <button
                      key={minutes}
                      type="button"
                      onClick={() => setTimeLimit(minutes)}
                      className={`py-4 rounded-xl font-medium transition-all ${
                        timeLimit === minutes
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`time-${minutes}min`}
                    >
                      {minutes} min
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* End Date/Time Option - Paid Only - Moved here */}
            <div>
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                <div className="flex-1">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Use End Date & Time
                    {!profile?.is_paid && (
                      <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white ml-2">
                        <Crown className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    {profile?.is_paid 
                      ? 'Set a specific end date and time instead of a countdown'
                      : 'Upgrade to Premium to set custom end dates'
                    }
                  </p>
                </div>
                <Switch
                  checked={useEndDatetime}
                  onCheckedChange={(checked) => {
                    if (!profile?.is_paid && checked) {
                      setShowPremiumModal(true);
                      return;
                    }
                    setUseEndDatetime(checked);
                  }}
                  disabled={!profile?.is_paid}
                />
              </div>
              
              {useEndDatetime && profile?.is_paid && (
                <div className="mt-3">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    End Date & Time <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    value={endDatetime}
                    onChange={(e) => setEndDatetime(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full text-lg py-6"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Session will end at this specific date and time
                  </p>
                </div>
              )}
            </div>

            {/* Max Participants Slider */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Max Participants
                </span>
                <span className="text-lg font-bold text-purple-600">{maxParticipants}</span>
              </Label>
              
              <div className="relative">
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={maxParticipants}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!profile?.is_paid && value > 10) {
                      toast.error('Upgrade to Premium for up to 100 participants!');
                      navigate('/subscription');
                      return;
                    }
                    setMaxParticipants(value);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #7c3aed ${(maxParticipants / 100) * 100}%, #e5e7eb ${(maxParticipants / 100) * 100}%)`
                  }}
                />
                
                {!profile?.is_paid && (
                  <div className="absolute top-0 left-[10%] w-px h-8 bg-red-500 pointer-events-none">
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Free Limit
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>1</span>
                <span>50</span>
                <span>100</span>
              </div>
              
              {!profile?.is_paid && maxParticipants > 5 && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-yellow-800 font-medium">Free users are limited to 10 participants</p>
                    <button
                      onClick={() => navigate('/subscription')}
                      className="text-purple-600 hover:text-purple-700 font-medium mt-1 underline"
                    >
                      Upgrade to Premium for up to 100 participants
                    </button>
                  </div>
                </div>
              )}
            </div>


            {/* Anonymous */}
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
              <div>
                <Label htmlFor="anonymous" className="text-sm font-medium text-gray-700">
                  Anonymous Mode
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Participants will be assigned random names
                </p>
              </div>
              <Switch
                id="anonymous"
                checked={isAnonymous}
                onCheckedChange={setIsAnonymous}
                data-testid="anonymous-switch"
              />
            </div>

            {/* Auto-save Indicator (only show in draft mode) */}
            {!isEditMode && autoSaving && (
              <div className="text-sm text-gray-500 text-center mb-4">
                💾 Saving draft...
              </div>
            )}
            {!isEditMode && lastSaved && !autoSaving && (
              <div className="text-sm text-gray-500 text-center mb-4">
                ✓ Draft saved {new Date(lastSaved).toLocaleTimeString()}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-6 rounded-xl"
              disabled={loading || (!isEditMode && !draftId)}
              data-testid="create-session-button"
            >
              {loading 
                ? (isEditMode ? 'Updating...' : 'Publishing...') 
                : (isEditMode ? 'Update Session' : 'Create & Start Session')
              }
            </Button>
          </form>
        </div>
      </div>

      {/* Session Limit Modal */}
      {showLimitModal && !profile?.is_paid && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="bg-gradient-to-br from-orange-400 to-red-500 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Session Limit Reached</h2>
                <p className="text-gray-600 mb-4">
                  You've created <span className="font-bold text-purple-600">{profile?.sessions_created_count}/10</span> sessions
                </p>
                <p className="text-gray-700">
                  Free users are limited to 10 sessions. Upgrade to Premium for unlimited sessions!
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-6"
                >
                  <Crown className="mr-2 h-5 w-5" />
                  Upgrade to Premium
                </Button>
                <Button
                  onClick={() => {
                    setShowLimitModal(false);
                    navigate('/dashboard');
                  }}
                  variant="outline"
                  className="w-full py-6"
                >
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Premium Feature Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full mb-4">
                  <Crown className="h-8 w-8 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Premium Feature</h2>
                <p className="text-gray-600">
                  Custom end date/time scheduling is available exclusively for Premium members
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-purple-900 mb-2">Premium Benefits:</h3>
                <ul className="space-y-2 text-sm text-purple-800">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Set specific end dates and times</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Unlimited sessions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Up to 100 participants</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Full access to all features</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-6"
                >
                  <Crown className="mr-2 h-5 w-5" />
                  Upgrade to Premium - $28/month
                </Button>
                <Button
                  onClick={() => setShowPremiumModal(false)}
                  variant="outline"
                  className="w-full py-6"
                >
                  Maybe Later
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SessionSetup;